import { useEffect, useState } from "react";
import { Button, Form, Offcanvas, Table } from "react-bootstrap";
import { FaEye, FaPenToSquare, FaPlus, FaTrash } from "react-icons/fa6";
import { toast } from "react-toastify";
import DocumentPreviewModal, { PreviewableDocument } from "../../common/DocumentPreviewModal";
import type { ExpertProfile } from "../../../types/Profiles/expertProfile.types";
import { updateExpertProfile } from "../../../services/expertProfile/expertProfile";
import { buildPreviewableDocument } from "../../../utilities/uploadFiles";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5002";

const expertServiceSuggestions = [
  "Bureau d'etudes automobile",
  "Ingenierie produit vehicule",
  "Ingenierie process industriel",
  "Homologation et reglementation automobile",
  "Essais et validation vehicule",
  "Qualite fournisseur APQP / PPAP",
  "Audit IATF 16949 / ISO 9001",
  "Industrialisation pieces plastiques",
  "Industrialisation pieces metalliques",
  "Methodes et temps industriels",
  "Documentation technique",
  "Formation maintenance automobile",
  "Diagnostic electronique embarque",
  "ADAS et systemes d'aide a la conduite",
  "Cybersecurite automobile",
  "NVH bruit vibration harshness",
  "Lean manufacturing automobile",
  "Gestion de projet localisation",
  "Sourcing technique fournisseurs",
  "Controle qualite et laboratoire metrologie",
];

const EmptyState = ({ label }: { label: string }) => (
  <div className="border rounded-4 p-4 text-muted bg-white">{label}</div>
);

const buildPayload = (
  profile: ExpertProfile,
  overrides: Partial<{
    domains: string[];
    certifications: string[];
    supportNeeds: string[];
  }> = {},
) => ({
  companyName: profile.identity.companyName,
  expertType: profile.identity.expertType,
  siteName: profile.identity.siteName,
  wilaya: profile.identity.wilaya,
  address: profile.identity.address,
  location: profile.identity.location,
  contactName: profile.identity.contactName,
  contactEmail: profile.identity.contactEmail,
  contactPhone: profile.identity.contactPhone,
  status: profile.identity.status,
  aidaBadge: profile.identity.aidaBadge,
  domains: overrides.domains ?? profile.domains,
  certifications: overrides.certifications ?? profile.certifications,
  supportNeeds: overrides.supportNeeds ?? profile.supportNeeds,
});

type ManagedListTabProps = {
  profile: ExpertProfile;
  userId?: number | string | null;
  onProfileUpdated?: () => Promise<void> | void;
  kind: "services";
};

function ManagedExpertListTab({ profile, userId, onProfileUpdated }: ManagedListTabProps) {
  const title = "Services et domaines d'expertise";
  const subtitle = "Prestations proposees par l'expert, le bureau d'etudes ou le cabinet technique.";
  const items = profile.domains;
  const datalistId = "expert-service-suggestions";
  const fieldLabel = "Service / domaine";
  const emptyLabel = "Aucun service declare.";

  const [showDrawer, setShowDrawer] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!showDrawer) {
      setEditingIndex(null);
      setValue("");
    }
  }, [showDrawer]);

  const openCreate = () => {
    setEditingIndex(null);
    setValue("");
    setShowDrawer(true);
  };

  const openEdit = (index: number) => {
    setEditingIndex(index);
    setValue(items[index] || "");
    setShowDrawer(true);
  };

  const saveItems = async (nextItems: string[]) => {
    const cleaned = Array.from(new Set(nextItems.map((item) => item.trim()).filter(Boolean)));
    setSaving(true);
    try {
      await updateExpertProfile(buildPayload(profile, { domains: cleaned }), userId);
      toast.success("Services mis a jour");
      setShowDrawer(false);
      await onProfileUpdated?.();
    } catch (error: any) {
      console.error(error);
      toast.error(error?.response?.data?.error || "Erreur pendant la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    const trimmed = value.trim();
    if (!trimmed) {
      toast.error("Veuillez saisir une valeur.");
      return;
    }
    const nextItems = [...items];
    if (editingIndex === null) nextItems.push(trimmed);
    else nextItems[editingIndex] = trimmed;
    await saveItems(nextItems);
  };

  const handleDelete = async (index: number) => {
    await saveItems(items.filter((_, currentIndex) => currentIndex !== index));
  };

  return (
    <>
      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body p-4">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
            <div>
              <h4 className="fw-semibold mb-1">{title}</h4>
              <p className="text-muted mb-0">{subtitle}</p>
            </div>
            <Button variant="warning" className="rounded-3 px-4 fw-semibold d-inline-flex align-items-center gap-2" onClick={openCreate}>
              <FaPlus size={14} /> Ajouter
            </Button>
          </div>

          {items.length ? (
            <div className="table-responsive">
              <Table className="dataTable table align-middle mb-0">
                <thead className="bg-white text-uppercase">
                  <tr className="ligth ligth-data">
                    <th style={{ width: 90 }}>#</th>
                    <th>{fieldLabel}</th>
                    <th className="text-center" style={{ width: 150 }}>Actions</th>
                  </tr>
                </thead>
                <tbody className="ligth-body">
                  {items.map((item, index) => (
                    <tr key={`${item}-${index}`}>
                      <td>{index + 1}</td>
                      <td className="fw-semibold">{item}</td>
                      <td className="text-center">
                        <button type="button" className="btn btn-sm btn-warning rounded-circle me-2" style={{ width: 36, height: 36, padding: 0 }} title="Modifier" onClick={() => openEdit(index)}>
                          <FaPenToSquare size={14} />
                        </button>
                        <button type="button" className="btn btn-sm btn-danger rounded-circle" style={{ width: 36, height: 36, padding: 0 }} title="Supprimer" onClick={() => handleDelete(index)}>
                          <FaTrash size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <EmptyState label={emptyLabel} />
          )}
        </div>
      </div>

      <Offcanvas show={showDrawer} onHide={() => setShowDrawer(false)} placement="end" backdrop style={{ width: window.innerWidth > 992 ? "42vw" : "100%" }}>
        <Offcanvas.Header closeButton className="border-bottom">
          <Offcanvas.Title className="fw-semibold">{editingIndex === null ? "Ajouter" : "Modifier"} un service</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Form>
            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold">{fieldLabel}</Form.Label>
              <Form.Control list={datalistId} value={value} onChange={(event) => setValue(event.target.value)} className="rounded-3" placeholder="Ex: Homologation et reglementation automobile" autoFocus />
              <datalist id={datalistId}>
                {expertServiceSuggestions.map((suggestion) => <option key={suggestion} value={suggestion} />)}
              </datalist>
              <Form.Text className="text-muted">Vous pouvez choisir une proposition ou saisir une valeur libre.</Form.Text>
            </Form.Group>

            <div className="border rounded-4 p-3 mb-4" style={{ background: "#fffdf5" }}>
              <h6 className="fw-semibold mb-2">Exemples pertinents automobile</h6>
              <div className="d-flex flex-wrap gap-2">
                {expertServiceSuggestions.slice(0, 8).map((suggestion) => (
                  <button key={suggestion} type="button" className="badge rounded-pill bg-light text-dark border px-3 py-2" onClick={() => setValue(suggestion)}>{suggestion}</button>
                ))}
              </div>
            </div>

            <div className="d-flex justify-content-between mt-4">
              <Button variant="light" onClick={() => setShowDrawer(false)} className="rounded-3 px-4">Annuler</Button>
              <Button variant="warning" onClick={handleSave} disabled={saving} className="rounded-3 px-4 fw-semibold">{saving ? "Enregistrement..." : "Enregistrer"}</Button>
            </div>
          </Form>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
}

export function ExpertServicesTab({ profile, userId, onProfileUpdated }: Omit<ManagedListTabProps, "kind">) {
  return <ManagedExpertListTab profile={profile} userId={userId} onProfileUpdated={onProfileUpdated} kind="services" />;
}

type Certification = {
  id: number;
  id_certification: number | string;
  certification_name: string;
  certificate_number: string;
  issued_date?: string;
  expiry_date?: string;
  certificate_file?: string;
  original_file_name?: string;
  is_required?: number | string;
  linked_product?: string;
  conformity_status?: string;
  export_impact?: string;
};

type CertificationType = {
  id_certification: number;
  name: string;
};

const emptyCertificationForm = {
  id_certification: "",
  certificate_number: "",
  issued_date: "",
  expiry_date: "",
  certificate_file: "",
  original_file_name: "",
  is_required: "0",
  linked_product: "",
  conformity_status: "",
  export_impact: "",
};

const formatDateForInput = (value?: string) => {
  if (!value) return "";
  return String(value).slice(0, 10);
};

const formatDate = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value).slice(0, 10) : date.toLocaleDateString("fr-FR");
};

export function ExpertCertificationsTab({ userId, onProfileUpdated }: Omit<ManagedListTabProps, "kind">) {
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [certificationTypes, setCertificationTypes] = useState<CertificationType[]>([]);
  const [showDrawer, setShowDrawer] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyCertificationForm);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isFileDragging, setIsFileDragging] = useState(false);
  const [previewFile, setPreviewFile] = useState<PreviewableDocument | null>(null);
  const [loading, setLoading] = useState(false);

  const loadCertifications = async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const response = await fetch(`${backendUrl}/api/experts/${userId}/certifications`);
      if (!response.ok) throw new Error("load failed");
      const data = await response.json();
      setCertifications(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      toast.error("Erreur de chargement des certifications");
    } finally {
      setLoading(false);
    }
  };

  const loadCertificationTypes = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/certification-types`);
      const data = await response.json();
      setCertificationTypes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      setCertificationTypes([]);
    }
  };

  useEffect(() => {
    loadCertifications();
    loadCertificationTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const resetForm = () => {
    setEditId(null);
    setForm(emptyCertificationForm);
    setSelectedFile(null);
    setIsFileDragging(false);
    setShowDrawer(false);
  };

  const openCreate = () => {
    setEditId(null);
    setForm(emptyCertificationForm);
    setSelectedFile(null);
    setShowDrawer(true);
  };

  const openEdit = (certification: Certification) => {
    setEditId(certification.id);
    setForm({
      id_certification: String(certification.id_certification || ""),
      certificate_number: certification.certificate_number || "",
      issued_date: formatDateForInput(certification.issued_date),
      expiry_date: formatDateForInput(certification.expiry_date),
      certificate_file: certification.certificate_file || "",
      original_file_name: certification.original_file_name || "",
      is_required: String(certification.is_required || 0),
      linked_product: certification.linked_product || "",
      conformity_status: certification.conformity_status || "",
      export_impact: certification.export_impact || "",
    });
    setSelectedFile(null);
    setShowDrawer(true);
  };

  const setCertificateUploadFile = (file?: File | null) => {
    if (!file) return;
    if (!/\.(pdf|jpg|jpeg|png)$/i.test(file.name)) {
      toast.error("Formats acceptes: PDF, JPG, JPEG, PNG");
      return;
    }
    setSelectedFile(file);
  };

  const handleCertificateDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsFileDragging(false);
    setCertificateUploadFile(event.dataTransfer.files?.[0]);
  };

  const buildCertificationFormData = () => {
    const formData = new FormData();
    formData.append("id_user", String(userId || ""));
    formData.append("id_certification", form.id_certification);
    formData.append("certificate_number", form.certificate_number);
    formData.append("issued_date", form.issued_date);
    formData.append("expiry_date", form.expiry_date);
    formData.append("is_required", form.is_required);
    formData.append("linked_product", form.linked_product);
    formData.append("linked_constructor", "");
    formData.append("conformity_status", form.conformity_status);
    formData.append("export_impact", form.export_impact);
    if (selectedFile) formData.append("certificate_file", selectedFile);
    return formData;
  };

  const saveCertification = async () => {
    if (!userId) {
      toast.error("Utilisateur expert introuvable");
      return;
    }
    if (!form.id_certification) {
      toast.error("Veuillez selectionner une certification");
      return;
    }

    try {
      const url = editId
        ? `${backendUrl}/api/experts/certifications/update/${editId}`
        : `${backendUrl}/api/experts/certifications/create`;
      const response = await fetch(url, {
        method: editId ? "PUT" : "POST",
        body: buildCertificationFormData(),
      });
      if (!response.ok) throw new Error("save failed");
      toast.success(editId ? "Certification modifiee" : "Certification ajoutee");
      resetForm();
      await loadCertifications();
      await onProfileUpdated?.();
    } catch (error) {
      console.error(error);
      toast.error("Erreur pendant la sauvegarde de la certification");
    }
  };

  const deleteCertification = async (id: number) => {
    try {
      const response = await fetch(`${backendUrl}/api/experts/certifications/${id}`, { method: "DELETE" });
      if (!response.ok) throw new Error("delete failed");
      toast.success("Certification supprimee");
      await loadCertifications();
      await onProfileUpdated?.();
    } catch (error) {
      console.error(error);
      toast.error("Erreur pendant la suppression de la certification");
    }
  };

  const openCertificatePreview = (certification: Certification) => {
    const document = buildPreviewableDocument(
      certification.certificate_file,
      certification.original_file_name || certification.certification_name,
      "certifications",
      "Certificat",
    );
    if (!document) {
      toast.info("Aucun document attache");
      return;
    }
    setPreviewFile(document);
  };

  return (
    <>
      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body p-4">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
            <div>
              <h4 className="fw-semibold mb-1">Certifications</h4>
              <p className="text-muted mb-0">Liste des certifications avec numero, validite, statut et document justificatif.</p>
            </div>
            <Button variant="warning" className="rounded-3 px-4 fw-semibold d-inline-flex align-items-center gap-2" onClick={openCreate}>
              <FaPlus size={14} /> Ajouter une certification
            </Button>
          </div>

          <div className="table-responsive">
            <Table className="dataTable table align-middle mb-0">
              <thead className="bg-white text-uppercase">
                <tr className="ligth ligth-data">
                  <th>Certification</th>
                  <th>Numero</th>
                  <th>Delivree le</th>
                  <th>Expiration</th>
                  <th>Service lie</th>
                  <th>Statut</th>
                  <th>Document</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="ligth-body">
                {certifications.length ? certifications.map((certification) => (
                  <tr key={certification.id}>
                    <td className="fw-semibold">{certification.certification_name || "-"}</td>
                    <td>{certification.certificate_number || "-"}</td>
                    <td>{formatDate(certification.issued_date)}</td>
                    <td>{formatDate(certification.expiry_date)}</td>
                    <td>{certification.linked_product || "-"}</td>
                    <td>{certification.conformity_status || "-"}</td>
                    <td>
                      {certification.certificate_file ? (
                        <Button variant="outline-secondary" size="sm" className="rounded-3 d-inline-flex align-items-center gap-2" onClick={() => openCertificatePreview(certification)}>
                          <FaEye size={13} /> Voir
                        </Button>
                      ) : (
                        <span className="text-muted">Aucun fichier</span>
                      )}
                    </td>
                    <td className="text-center">
                      <button type="button" className="btn btn-sm btn-warning rounded-circle me-2" style={{ width: 36, height: 36, padding: 0 }} title="Modifier" onClick={() => openEdit(certification)}>
                        <FaPenToSquare size={14} />
                      </button>
                      <button type="button" className="btn btn-sm btn-danger rounded-circle" style={{ width: 36, height: 36, padding: 0 }} title="Supprimer" onClick={() => deleteCertification(certification.id)}>
                        <FaTrash size={14} />
                      </button>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={8} className="text-center text-muted py-4">
                      {loading ? "Chargement..." : "Aucune certification ajoutee."}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>
      </div>

      <Offcanvas show={showDrawer} onHide={resetForm} placement="end" backdrop style={{ width: window.innerWidth > 992 ? "50vw" : "100%" }}>
        <Offcanvas.Header closeButton className="border-bottom">
          <Offcanvas.Title className="fw-semibold">{editId ? "Modifier la certification" : "Ajouter une certification"}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Form>
            <div className="row g-3">
              <Form.Group className="col-md-6">
                <Form.Label className="fw-semibold">Certification</Form.Label>
                <Form.Select value={form.id_certification} onChange={(event) => setForm({ ...form, id_certification: event.target.value })} className="rounded-3">
                  <option value="">Selectionner une certification</option>
                  {certificationTypes.map((certificationType) => (
                    <option key={certificationType.id_certification} value={certificationType.id_certification}>
                      {certificationType.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="col-md-6">
                <Form.Label className="fw-semibold">Numero du certificat</Form.Label>
                <Form.Control value={form.certificate_number} onChange={(event) => setForm({ ...form, certificate_number: event.target.value })} className="rounded-3" />
              </Form.Group>
              <Form.Group className="col-md-6">
                <Form.Label className="fw-semibold">Date de delivrance</Form.Label>
                <Form.Control type="date" value={form.issued_date} onChange={(event) => setForm({ ...form, issued_date: event.target.value })} className="rounded-3" />
              </Form.Group>
              <Form.Group className="col-md-6">
                <Form.Label className="fw-semibold">Date d'expiration</Form.Label>
                <Form.Control type="date" value={form.expiry_date} onChange={(event) => setForm({ ...form, expiry_date: event.target.value })} className="rounded-3" />
              </Form.Group>
              <Form.Group className="col-md-6">
                <Form.Label className="fw-semibold">Service lie</Form.Label>
                <Form.Control list="expert-certification-services" value={form.linked_product} onChange={(event) => setForm({ ...form, linked_product: event.target.value })} className="rounded-3" placeholder="Ex: Audit IATF 16949" />
                <datalist id="expert-certification-services">
                  {expertServiceSuggestions.map((service) => <option key={service} value={service} />)}
                </datalist>
              </Form.Group>
              <Form.Group className="col-md-6">
                <Form.Label className="fw-semibold">Certification obligatoire</Form.Label>
                <Form.Select value={form.is_required} onChange={(event) => setForm({ ...form, is_required: event.target.value })} className="rounded-3">
                  <option value="0">Non</option>
                  <option value="1">Oui</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="col-md-6">
                <Form.Label className="fw-semibold">Statut de conformite</Form.Label>
                <Form.Select value={form.conformity_status} onChange={(event) => setForm({ ...form, conformity_status: event.target.value })} className="rounded-3">
                  <option value="">A definir</option>
                  <option value="conforme">Conforme</option>
                  <option value="a_renouveler">A renouveler</option>
                  <option value="expire">Expire</option>
                  <option value="en_cours">En cours</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="col-md-6">
                <Form.Label className="fw-semibold">Impact export / mission</Form.Label>
                <Form.Control value={form.export_impact} onChange={(event) => setForm({ ...form, export_impact: event.target.value })} className="rounded-3" />
              </Form.Group>
              <Form.Group className="col-12">
                <Form.Label className="fw-semibold">Document du certificat</Form.Label>
                <input id="expertCertificateFileInput" type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: "none" }} onChange={(event) => setCertificateUploadFile(event.target.files?.[0])} />
                <div
                  role="button"
                  tabIndex={0}
                  className={`border rounded-4 p-4 bg-light ${isFileDragging ? "border-warning" : ""}`}
                  onClick={() => document.getElementById("expertCertificateFileInput")?.click()}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") document.getElementById("expertCertificateFileInput")?.click();
                  }}
                  onDragEnter={(event) => { event.preventDefault(); setIsFileDragging(true); }}
                  onDragOver={(event) => { event.preventDefault(); setIsFileDragging(true); }}
                  onDragLeave={() => setIsFileDragging(false)}
                  onDrop={handleCertificateDrop}
                >
                  <div className="fw-semibold">Choisir un fichier ou glisser-deposer</div>
                  <div className="text-muted small">PDF, JPG, JPEG, PNG</div>
                  <div className="mt-2">
                    {selectedFile ? selectedFile.name : editId && form.original_file_name ? `Fichier actuel: ${form.original_file_name}` : "Aucun fichier selectionne"}
                  </div>
                </div>
              </Form.Group>
            </div>
          </Form>
        </Offcanvas.Body>
        <div className="border-top p-3 d-flex justify-content-between">
          <Button variant="light" onClick={resetForm} className="rounded-3 px-4">Annuler</Button>
          <Button variant="warning" onClick={saveCertification} className="rounded-3 px-4 fw-semibold">Enregistrer</Button>
        </div>
      </Offcanvas>

      <DocumentPreviewModal document={previewFile} fallbackTitle="Document du certificat" onHide={() => setPreviewFile(null)} />
    </>
  );
}

export function ExpertActivityTab({ profile }: { profile: ExpertProfile }) {
  return (
    <div className="card border-0 shadow-sm rounded-4">
      <div className="card-body p-4">
        <h4 className="fw-semibold mb-1">Activite</h4>
        <p className="text-muted mb-4">Historique et informations de suivi du profil expert.</p>
        {profile.activity.length ? (
          <div className="list-group list-group-flush">
            {profile.activity.map((item) => (
              <div className="list-group-item px-0 d-flex justify-content-between gap-3" key={`${item.label}-${item.value}`}>
                <span className="fw-semibold">{item.label}</span>
                <span className="text-muted text-end">{item.value}</span>
              </div>
            ))}
          </div>
        ) : <EmptyState label="Aucune activite disponible." />}
      </div>
    </div>
  );
}

export function ExpertAidaAdhesionTab({ profile }: { profile: ExpertProfile }) {
  return (
    <div className="card border-0 shadow-sm rounded-4">
      <div className="card-body p-4">
        <h4 className="fw-semibold mb-1">Adhesion AIDA</h4>
        <p className="text-muted mb-4">Informations issues du dossier d'adhesion expert.</p>
        <div className="row g-4">
          <div className="col-md-6"><div className="border rounded-4 p-4"><small className="text-muted d-block mb-1">ID adhesion</small><div className="fw-semibold">{profile.identity.adhesionId || "-"}</div></div></div>
          <div className="col-md-6"><div className="border rounded-4 p-4"><small className="text-muted d-block mb-1">Statut</small><div className="fw-semibold">{profile.identity.status || "-"}</div></div></div>
          <div className="col-md-6"><div className="border rounded-4 p-4"><small className="text-muted d-block mb-1">Badge AIDA</small><div className="fw-semibold">{profile.identity.aidaBadge || "-"}</div></div></div>
          <div className="col-md-6"><div className="border rounded-4 p-4"><small className="text-muted d-block mb-1">Besoins d'accompagnement</small><div className="fw-semibold">{profile.supportNeeds.length}</div></div></div>
        </div>
      </div>
    </div>
  );
}
